import type { User } from '@gmn-portal/backend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { payloadClient } from '.';
import type { FindPayloadApiClientParams } from './types';

export type UserQueryParams = FindPayloadApiClientParams<'users', User> & { disabled?: boolean };

export const useUsers = (params?: UserQueryParams) => {
  return useQuery({
    queryKey: ['users', params],
    enabled: !params?.disabled,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      const response = await payloadClient.find({
        collection: 'users',
        ...params,
      });
      return response;
    },
  });
};

export const useUser = (id: string, params?: { disabled?: boolean }) => {
  return useQuery({
    queryKey: ['users', 'detail', id],
    enabled: !params?.disabled && !!id,
    queryFn: async () => {
      const response = await payloadClient.findById({
        collection: 'users',
        id,
      });
      return response;
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<User> }) => {
      const response = await payloadClient.updateById({
        collection: 'users',
        id,
        data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};
